import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Printer, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { usePageMeta } from "@/lib/seo";
import { fmtHeight, fmtMeasure } from "@/lib/units";

const BK = process.env.REACT_APP_BACKEND_URL;
const src = (u) => (u && u.startsWith("/api/") ? `${BK}${u}` : u);

export default function CompCard() {
  usePageMeta({ title: "Comp Card | Solis Casting", noindex: true });
  const { id } = useParams();
  const [model, setModel] = useState(null);

  useEffect(() => {
    api.get(`/models/${id}`).then((r) => setModel(r.data)).catch(() => {});
  }, [id]);

  if (!model) return <div className="px-6 sm:px-10 py-32 overline-label">Setting the card…</div>;

  const photos = [model.photo_url, ...(model.photos || []).filter((p) => p !== model.photo_url)].filter(Boolean);
  const cover = photos[0];
  const rest = photos.slice(1, 5);
  const stats = [
    ["Height", fmtHeight(model.height_cm)],
    ["Bust / Chest", fmtMeasure(model.bust)],
    ["Waist", fmtMeasure(model.waist)],
    ["Hips", fmtMeasure(model.hips)],
    ["Shoe", model.shoe],
    ["Hair", model.hair],
    ["Eyes", model.eyes],
    ["Based in", model.city],
  ].filter(([, v]) => v);

  return (
    <div className="px-6 sm:px-10 py-24" data-testid="comp-card-page">
      <div className="flex justify-between items-center mb-12 no-print">
        <Link to={`/models/${id}`} data-testid="back-to-profile" className="overline-label inline-flex items-center gap-2 hover:text-foreground transition-colors">
          <ArrowLeft size={14} /> Back to profile
        </Link>
        <button data-testid="print-comp-card" onClick={() => window.print()} className="btn-primary !py-3">
          <Printer size={14} /> Print / Save PDF
        </button>
      </div>

      <div className="print-card bg-white text-black max-w-5xl mx-auto p-8 sm:p-12" data-testid="comp-card">
        <div className="flex items-baseline justify-between border-b border-black/15 pb-5 mb-8">
          <p className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-black/50">Solis Casting</p>
          <p className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-black/50">Comp Card — MMXXVI</p>
        </div>

        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 sm:col-span-7">
            {cover && <img src={src(cover)} alt={model.name} className="w-full aspect-[3/4] object-cover" data-testid="comp-card-cover" />}
          </div>
          <div className="col-span-12 sm:col-span-5 grid grid-cols-2 gap-6 content-start">
            {rest.map((p, i) => (
              <img key={p} src={src(p)} alt={`${model.name} ${i + 2}`} className="w-full aspect-[3/4] object-cover" />
            ))}
            {rest.length === 0 && <div className="col-span-2 aspect-[3/4] bg-black/5 flex items-center justify-center font-display italic text-black/30 text-xl">More frames soon</div>}
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6 mt-8 items-end">
          <div className="col-span-12 sm:col-span-5">
            <h1 className="font-display font-light text-4xl sm:text-5xl tracking-tight leading-none" data-testid="comp-card-name">{model.name}</h1>
            {model.instagram && <p className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-black/50 mt-3">{model.instagram}</p>}
          </div>
          <div className="col-span-12 sm:col-span-7 grid grid-cols-4 gap-x-4 gap-y-5">
            {stats.map(([label, value]) => (
              <div key={label}>
                <p className="font-grotesk text-[9px] uppercase tracking-[0.2em] text-black/45 mb-1">{label}</p>
                <p className="font-display text-lg leading-none">{value}</p>
              </div>
            ))}
          </div>
        </div>

        {(model.work_history || []).length > 0 && (
          <div className="mt-8 border-t border-black/15 pt-5">
            <p className="font-grotesk text-[9px] uppercase tracking-[0.2em] text-black/45 mb-4">Selected work</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2">
              {model.work_history.slice(0, 6).map((w, i) => (
                <p key={i} className="text-xs text-black/70">
                  <span className="font-medium">{w.client}</span>
                  {w.role ? ` — ${w.role}` : ""}{w.year ? `, ${w.year}` : ""}
                </p>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
